import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import { connect, useDispatch } from 'react-redux';
import { Button, Container, Grid, makeStyles, Typography } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { getTherapy, getTherapies, postTherapy, putTherapy } from '../../store/actions/therapy/therapy_action';
import { REACT_ROUTES } from '../../constants/react_routes';
import history from '../../services/history';
import TextInput from '../ui_components/text_input';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles((theme) => ({
	container: {
		padding: theme.spacing(3, 2)
	},
    typography: {
		margin: theme.spacing(3, 0, 2)
    },
    form: {
		width: '100%', // Fix IE 11 issue.
		marginTop: theme.spacing(1),
        '& p': {
            color: '#bf1650',
            '&::before': {
                display: 'inline',
                content: '"⚠ "'
            }
        }
	}
}));

const showErrorsAndMessages = (violations, enqueueSnackbar, setError) => {
    if (!violations) {
        enqueueSnackbar('Uspešno ste sačuvali terapiju', { variant: 'success' });
        return history.push(REACT_ROUTES.THERAPIES);
    }
    for (const violation of violations) {
        enqueueSnackbar(violation?.message, { variant: 'warning' });
        if (violation?.propertyPath) {
            setError(violation?.propertyPath, { message: violation?.message });
        }
    }
}

const Therapy = ({ therapies, handleClose, initialize }) => {
    const { uuid } = useParams();
    const { container, typography, form } = useStyles();
    const { control, handleSubmit, reset, setError, formState: { errors } } = useForm();
    const dispatch = useDispatch();

    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        uuid && (async () => {
            reset(await getTherapy(uuid));
        })();
        !therapies.collection.length && dispatch(getTherapies());
    }, [uuid]);

    const onSubmit = async (data) => {
        if (uuid && !handleClose && !initialize) {
            const { violations } = await dispatch(putTherapy(uuid, data));
            return showErrorsAndMessages(violations, enqueueSnackbar, setError);
        }
        const response = await dispatch(postTherapy(data));
        const { violations } = response;
        if (!violations && handleClose && initialize) {
            initialize(response);
            return handleClose();
        }
        return showErrorsAndMessages(violations, enqueueSnackbar, setError);
    }

    const handleClickBackToList = () => {
        handleClose ? handleClose() : history.push(REACT_ROUTES.THERAPIES);
    }

    return (
        <Container className={container}>
            <Typography align="center" variant="h4" className={typography}>Terapija</Typography>
            <Grid container justify="center" spacing={3}>
                <Grid item>
                    <Button
                        color="primary"
                        size="small"
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        type="button"
                    >
                        Sačuvaj
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={handleClickBackToList}
                    >
                        Nazad
                    </Button>
                </Grid>
            </Grid>
            <form className={form}>
                <Grid container justify="center" spacing={3}>
                    <Grid item xs={8}>
                        <TextInput
                            control={control}
                            name="name"
                            label="Naziv"
                            rules={{ required: 'Unesite naziv' }}
                        />
                        <ErrorMessage errors={errors} name="name" as="p" />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput
                            control={control}
                            name="dose"
                            type="text"
                            label="Doza (u miligramima)"
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput
                            name="wayOfUse"
                            control={control}
                            label="Način primene"
                            multiline
                            rows={3}
                            variant="outlined"
                        />
                    </Grid>
                </Grid>
            </form>
        </Container>
    )
}

Therapy.propTypes = {
    therapies: PropTypes.object,
    handleClose: PropTypes.func,
    initialize: PropTypes.func
}

const mapStateToProps = (state) => ({
    therapies: state.therapies
});

export default connect(mapStateToProps)(Therapy);
